/**
 * One `Idempotency-Key` per *user action*, reused on every retry of it.
 *
 * The server binds a key to the caller, the operation and the request body,
 * and answers a retry with the first response byte-for-byte (same run, same
 * import) instead of doing the work again. That only helps if a retry carries
 * the same key: a key minted inside `mutationFn` makes every "Try again" a new
 * request, and the server would queue a second run or start a second import.
 *
 * Invariant: `keyFor(input)` returns the same key for the same canonical input
 * until `settle()` is called (after the action succeeded). Different input is
 * a different action and gets a fresh key, so it is never refused as
 * `IDEMPOTENCY_KEY_REUSED`. A refusal keeps the key: the server releases keys
 * of refused requests, and a transient failure or `IDEMPOTENCY_REQUEST_IN_PROGRESS`
 * must be retried with the same one. A double click while the first request is
 * pending therefore sends the same key twice, and the server dedupes it.
 */
import { useState } from 'react';

export interface ActionKey {
  /** The key for this input: the current one if the input is unchanged. */
  keyFor(input?: unknown): string;
  /** The action succeeded; the next call starts a new action. */
  settle(): void;
}

/** Key order independent JSON, so `{a, b}` and `{b, a}` are the same action. */
export function canonicalJson(value: unknown): string {
  if (value === undefined) return 'null';
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
}

/** A key holder outside React (tests, non-hook callers). */
export function createActionKey(newKey: () => string = () => crypto.randomUUID()): ActionKey {
  let current: { readonly fingerprint: string; readonly key: string } | null = null;
  return {
    keyFor(input?: unknown): string {
      const fingerprint = canonicalJson(input);
      if (!current || current.fingerprint !== fingerprint) {
        current = { fingerprint, key: newKey() };
      }
      return current.key;
    },
    settle(): void {
      current = null;
    },
  };
}

/** {@link createActionKey}, stable for the component's lifetime. */
export function useActionKey(): ActionKey {
  const [actionKey] = useState(() => createActionKey());
  return actionKey;
}
